<template>
  <div
    v-if="chunk.application"
    class="card"
    style="width: 100%"
  >
    <div class="card-header">
      <div class="card-header-title is-size-2 is-center">
        {{ $t("applications.functions.uploadBundleTitle",
              { applicationName: chunk.application.localName || chunk.application.name }) }}
      </div>
    </div>
    <BProgress
      data-cy="total-progress"
      :value="percentProcessed"
      size="is-large"
      :type="percentProcessed === 100 ? 'is-success' : 'is-warning'"
      format="percent"
      show-value
    />
    <div class="card-content is-size-4">
      <b-checkbox
        v-model="hideDone"
      >
        {{ $t("applications.functions.uploadBundleHideDone") }}
      </b-checkbox>
    </div>
    <ul class="card-content list">
      <li
        v-for="datatype in filteredDatatypes"
        :key="datatype"
        :data-cy="`datatype-${datatype}`"
        class="datatype-row"
      >
        <div class="datatype-label is-size-4">
          {{ chunk.listDatatypesNames[datatype] }}
        </div>
        <ul>
          <li
            v-for="fileStatus in filteredProgresses?.[datatype]"
            :key="fileStatus.file"
            :data-cy="`file-${fileStatus.file}`"
            class="card-content is-size-5"
            :title="fileStatus.file"
          >
            <BIcon
              v-if="fileStatus.status === 'DONE'"
              icon="check-circle"
              type="is-success"
              size="is-medium"
            />
            <BIcon
              v-else-if="fileStatus.status === 'ERROR'"
              icon="times-circle"
              type="is-danger"
              size="is-medium"
            />
            <BIcon
              v-else-if="fileStatus.status === 'PENDING'"
              icon="spinner"
              custom-class="fa-spin"
              size="is-medium"
            />
            <span style="margin-left: 2em">{{ fileStatus.file }}</span>
            <span
              v-if="fileStatus.message"
              v-html="' ' + fileStatus.message"
            />
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { ProgressEntry, ResultProgress } from "@/types/front/ResultProgress";
import { PropType } from "vue";
import { computed, ref } from "vue";

export default {
  name: "BundleReport",
  props: {
    chunk: {
      type: Object as PropType<ResultProgress>,
      required: true,
    },
  },
  setup(props) {
    const hideDone = ref(false);

    const percentProcessed = computed(() => {
      const progresses = props.chunk.progresses;
      if (!progresses) return 0;
      const allFiles = Object.values(progresses).flat();
      const processedFilesCount = allFiles.filter(f =>
        f.status === "DONE" || f.status === "ERROR",
      ).length;
      const totalFilesCount = allFiles.length;
      return totalFilesCount > 0
        ? Math.round((processedFilesCount / totalFilesCount) * 100)
        : 0;
    });

    // Filtrer fichiers et datatypes pour masquer les DONE
    const filteredDatatypes = computed(() => {
      const progresses = props.chunk.progresses || {};
      const datatypes = props.chunk.listDatatypes || [];
      if (!hideDone.value) return datatypes;

      // Affiche uniquement les datatypes qui ont au moins un fichier non DONE
      return datatypes.filter((dt) => {
        const files = progresses[dt] || [];
        return files.some(f => f.status !== "DONE");
      });
    });

    const filteredProgresses = computed(() => {
      const progresses = props.chunk.progresses || {};
      if (!hideDone.value) return progresses;
      const res = {} as Record<string, ProgressEntry[]>;
      for (const dt of filteredDatatypes.value) {
        res[dt] = (progresses[dt] || []).filter(f => f.status !== "DONE");
      }
      return res;
    });

    return {
      percentProcessed,
      hideDone,
      filteredDatatypes,
      filteredProgresses,
    };
  },
};
</script>
